import type { Document, DocumentInfo, SearchResult, StorageOptions, StorageEvent } from './types'

/**
 * Common storage interface for all platforms
 * Single Responsibility: Define contract for storage operations
 */
export interface IStorage {
  // Core CRUD operations
  save(id: string, content: string, metadata?: Partial<Document['metadata']>): Promise<void>
  load(id: string, force?: boolean): Promise<Document>
  delete(id: string): Promise<void>
  list(): Promise<DocumentInfo[]>
  
  // Search operations
  search(query: string): Promise<SearchResult[]>
  searchByTags?(tags: string[]): Promise<DocumentInfo[]>
  
  // Utility operations
  exists(id: string): Promise<boolean>
  getInfo(id: string): Promise<DocumentInfo | null>
  
  // Bulk operations
  deleteAll?(): Promise<void>
  exportAll?(): Promise<Document[]>
  importDocuments?(documents: Document[]): Promise<void>
  
  // Event handling
  addEventListener?(event: StorageEvent['type'], handler: (event: StorageEvent) => void): void
  removeEventListener?(event: StorageEvent['type'], handler: (event: StorageEvent) => void): void
}

/**
 * Base storage class with common functionality
 */
export abstract class BaseStorage implements IStorage {
  protected options: StorageOptions
  protected eventHandlers: Map<string, Set<(event: StorageEvent) => void>> = new Map()
  
  constructor(options: StorageOptions = {}) {
    this.options = {
      autoSave: true,
      autoSaveInterval: 30000, // 30 seconds
      maxDocuments: 1000,
      maxSizePerDoc: 10 * 1024 * 1024, // 10MB
      ...options
    }
  }
  
  // Abstract methods to be implemented by subclasses
  abstract save(id: string, content: string, metadata?: Partial<Document['metadata']>): Promise<void>
  abstract load(id: string): Promise<Document>
  abstract delete(id: string): Promise<void>
  abstract list(): Promise<DocumentInfo[]>
  abstract search(query: string): Promise<SearchResult[]>
  abstract exists(id: string): Promise<boolean>
  abstract getInfo(id: string): Promise<DocumentInfo | null>
  
  // Helper methods
  protected generateId(): string {
    return `doc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }
  
  protected extractTitle(content: string): string {
    // Extract first line as title, remove markdown headers
    const firstLine = content.split('\n')[0] || '제목 없음'
    return firstLine.replace(/^#*\s*/, '').substring(0, 100)
  }
  
  protected extractPreview(content: string, maxLength: number = 200): string {
    // Remove markdown syntax and extract preview
    const plain = content
      .replace(/^#*\s*/gm, '') // Remove headers
      .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold
      .replace(/\*(.*?)\*/g, '$1') // Remove italic
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Remove links
      .replace(/```[\s\S]*?```/g, '') // Remove code blocks
      .replace(/`([^`]+)`/g, '$1') // Remove inline code
      .trim()
    
    return plain.length > maxLength 
      ? plain.substring(0, maxLength) + '...'
      : plain
  }
  
  protected calculateWordCount(content: string): number {
    // Korean word count (handles mixed Korean/English)
    const korean = content.match(/[\uAC00-\uD7AF]+/g) || []
    const english = content.match(/[a-zA-Z]+/g) || []
    return korean.length + english.length
  }
  
  protected calculateCharacterCount(content: string): number {
    // Count actual characters (excluding spaces and newlines)
    return content.replace(/[\s\n]/g, '').length
  }
  
  // Event handling
  addEventListener(event: StorageEvent['type'], handler: (event: StorageEvent) => void): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set())
    }
    this.eventHandlers.get(event)!.add(handler)
  }
  
  removeEventListener(event: StorageEvent['type'], handler: (event: StorageEvent) => void): void {
    this.eventHandlers.get(event)?.delete(handler)
  }
  
  protected emit(event: StorageEvent): void {
    const handlers = this.eventHandlers.get(event.type)
    if (handlers) {
      handlers.forEach(handler => handler(event))
    }
  }
}